import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import { ApiResponse } from "../models/ApiResponse";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
    /**
     * @description property to share vue instance
     */
    public static vueInstance: App;

    /**
     * @description initialize vue axios
     */
    public static init(app: App<Element>) {
        ApiService.vueInstance = app;
        ApiService.vueInstance.use(VueAxios, axios);
        ApiService.vueInstance.axios.defaults.baseURL =
            process.env.VUE_APP_API_URL;
        // console.log("ApiService -> init -> process.env.VUE_APP_API_URL", process.env.VUE_APP_API_URL);
        // ApiService.vueInstance.axios.defaults.baseURL = window.location.origin;
    }

    /**
     * @description set the default HTTP request headers
     */
    public static setHeader(): void {
        const token = JwtService.getToken();
        // A login response without an access token lands in localStorage as
        // the literal string "undefined"/"null", which is truthy: treat those
        // as no token at all so we never send `Bearer undefined`.
        if (token && token !== "undefined" && token !== "null") {
            ApiService.vueInstance.axios.defaults.headers.common[
                "Authorization"
            ] = `Bearer ${token}`;
        } else {
            ApiService.clearHeader();
        }
        // setFormDataHeader() pins a multipart Content-Type on the shared
        // defaults; drop it again here so a later JSON request is not sent
        // with the (boundary-less) multipart type left over from an upload.
        delete ApiService.vueInstance.axios.defaults.headers.common[
            "Content-Type"
        ];
    }

    /**
     * @description remove the Authorization header from the shared axios
     * defaults. Sending a stale or empty token makes the back-end reject the
     * request (401) even on endpoints that allow anonymous access.
     */
    public static clearHeader(): void {
        delete ApiService.vueInstance.axios.defaults.headers.common[
            "Authorization"
        ];
    }

    /**
     * @description set the default HTTP request headers
     */
    public static setFormDataHeader(): void {
        ApiService.setHeader();
        ApiService.vueInstance.axios.defaults.headers.common["Content-Type"] =
            "multipart/form-data";
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static query(
        resource: string,
        params: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios
            .get(resource, params)
            .catch((error) => {
                throw new Error(`[KT] ApiService ${error}`);
            });
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param slug: string
     * @returns Promise<AxiosResponse>
     */
    public static get(
        resource: string,
        slug = "" as string
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios
            .get(`${resource}/${slug}`)
            .catch((error) => {
                throw new Error(`[KT] ApiService ${error}`);
            });
    }

    /**
     * @description set the POST HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static post(
        resource: string,
        params: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.post(`${resource}`, params);
    }

    /**
 * @description send the GET HTTP request
 * @param resource: string
 * @param slug: string
 * @returns Promise<AxiosResponse>
 */
    public static getT<T>(
        resource: string,
        slug = "" as string
    ): Promise<AxiosResponse<ApiResponse<T>>> {
        return ApiService.vueInstance.axios
            .get<ApiResponse<T>>(`${resource}/${slug}`)
            .catch((error) => {
                throw new Error(`[KT] ApiService ${error}`);
            });
    }

    /**
     * @description set the POST HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static postT<T>(
        resource: string,
        data: any
    ): Promise<AxiosResponse<ApiResponse<T>>> {
        return ApiService.vueInstance.axios.post<ApiResponse<T>>(`${resource}`, data);
    }

    /**
     * @description send the UPDATE HTTP request
     * @param resource: string
     * @param slug: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static update(
        resource: string,
        slug: string,
        params: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
    }

    /**
     * @description Send the PUT HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static put(
        resource: string,
        params: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.put(`${resource}`, params);
    }

    /**
     * @description Send the PATCH HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    public static patch(
        resource: string,
        params: AxiosRequestConfig
    ): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.patch(`${resource}`, params);
    }

    /**
     * @description Send the DELETE HTTP request
     * @param resource: string
     * @returns Promise<AxiosResponse>
     */
    public static delete(resource: string): Promise<AxiosResponse> {
        return ApiService.vueInstance.axios.delete(resource).catch((error) => {
            throw new Error(`[RWV] ApiService ${error}`);
        });
    }
}

export default ApiService;
